<script>
import api from "@/helpers/api-rest/api.js";
import Swal from "sweetalert2";
import {
  generateValidations,
  onlyLetters,
  noSpecialChars,
  noSpaces,
} from "@/helpers/validations/validationsHelpers";
import { errorMessages } from "@/helpers/validations/errorMessages";
import { sameAs } from "vuelidate/lib/validators";
import "vue-select/dist/vue-select.css";
import vSelect from "vue-select";
import InstitutionalLogosBanner from "@/components/institutional-logos-banner.vue";

export default {
  components: { 
    vSelect,
    InstitutionalLogosBanner,
  },
  data() {
    return {
      selectProjectOptions: [],
      selectAutonomousCommunityOptions: [],
      userRegistered: {
        password: "",
        repassword: "",
        name: "",
        email: "",
        dni: "",
        autonomous_community_id: "",
        center: "",
        project_id: "",
        use: "",
      },
      loading: false,
      errorMessages,
      showPassword: false,
      showRePassword: false,
    };
  },
  validations: {
    userRegistered: generateValidations(
      [
        "name",
        "dni",
        "email",
        "password",
        "repassword",
        "autonomous_community_id",
        "center",
        "project_id",
        "use",
      ],
      {
        name: [onlyLetters],
        dni: [noSpecialChars, noSpaces],
        email: [noSpaces],
        repassword: [sameAs("password")],
      }
    ),
  },
  created() {
    document.body.classList.add("auth-body-bg");
    this.loadData();
  },
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    toggleRePasswordVisibility() {
      this.showRePassword = !this.showRePassword;
    },
    successmsg() {
      Swal.fire(
        "¡Enhorabuena!",
        "Tu solicitud ha sido registrada correctamente. Se le enviará un email cuando se acepte su solicitud y podrá tener acceso a la plataforma",
        "success"
      ).then(() => {
        this.$router.push({ name: "login" });
      });
    },
    loadProjects() {
      const apiEndpoint = `/api/v1/list-register?status=1`;
      api
        .get({ url: apiEndpoint })
        .then((response) => {
          this.selectProjectOptions = response?.data?.data;
        })
        .catch((error) => {
          console.error("Error loading data:", error);
        });
    },
    loadAutonomousCommunities() {
      const apiEndpoint = `/api/v1/autonomous-communities`;
      api
        .get({ url: apiEndpoint })
        .then((response) => {
          this.selectAutonomousCommunityOptions = response?.data?.data || [];
        })
        .catch((error) => {
          console.error("Error loading autonomous communities:", error);
        });
    },
    registerUser() {
      this.loading = true;
      const apiEndpoint = `/api/v1/register`;
      api
        .post({ url: apiEndpoint, data: this.userRegistered })
        .then(() => {
          this.loading = false;
          this.successmsg();
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al crear el usuario:", error);          
        });
    },
    submitForm() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.registerUser();
    },
    loadData() {
      this.loadProjects();
      this.loadAutonomousCommunities();
    },
  },
};
</script>

<template>
  <div class="auth-page-wrapper">
    <!-- Background decorative elements -->
    <div class="auth-background">
      <div class="auth-shape auth-shape-1"></div>
      <div class="auth-shape auth-shape-2"></div>
      <div class="auth-shape auth-shape-3"></div>
    </div>

    <!-- Main content -->
    <div class="auth-container">
      <div class="auth-card auth-card-register">
        <!-- Logo Section -->
        <div class="auth-logo-section">
          <div class="auth-logo-wrapper">
            <img
              src="@/assets/images/logo-CEX.png"
              alt="CEX Logo"
              class="auth-main-logo"
            />
          </div>
          <h1 class="auth-welcome-title">Crea tu cuenta</h1>
          <p class="auth-welcome-subtitle">
            Completa el formulario para solicitar acceso a la plataforma
          </p>
        </div>

        <!-- Register Form -->
        <form class="auth-form" @submit.prevent="submitForm">
          <!-- Name Field -->
          <div class="auth-form-group">
            <label for="name" class="auth-label">
              <i class="ri-user-line auth-label-icon"></i>
              Nombre <span class="auth-required">*</span>
            </label>
            <div class="auth-input-wrapper">
              <input
                type="text"
                v-model="userRegistered.name"
                class="auth-input"
                id="name"
                placeholder="Introduce tu nombre"
                maxlength="200"
                :class="{ 'auth-input-error': $v.userRegistered.name.$error }"
              />
              <div v-if="$v.userRegistered.name.$error" class="auth-error-message">
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.userRegistered.name.required">{{
                  errorMessages.required
                }}</span>
                <span v-else-if="!$v.userRegistered.name.onlyLetters">{{
                  errorMessages.onlyLetters
                }}</span>
              </div>
            </div>
          </div>

          <!-- DNI Field -->
          <div class="auth-form-group">
            <label for="userDNI" class="auth-label">
              <i class="ri-account-box-line auth-label-icon"></i>
              DNI <span class="auth-required">*</span>
            </label>
            <div class="auth-input-wrapper">
              <input
                type="text"
                v-model="userRegistered.dni"
                class="auth-input"
                id="userDNI"
                placeholder="Introduce DNI"
                maxlength="10"
                :class="{ 'auth-input-error': $v.userRegistered.dni.$error }"
              />
              <div v-if="$v.userRegistered.dni.$error" class="auth-error-message">
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.userRegistered.dni.required">{{
                  errorMessages.required
                }}</span>
                <span v-else-if="!$v.userRegistered.dni.noSpecialChars">{{
                  errorMessages.noSpecialChars
                }}</span>
              </div>
            </div>
          </div>

          <!-- Email Field -->
          <div class="auth-form-group">
            <label for="email" class="auth-label">
              <i class="ri-mail-line auth-label-icon"></i>
              Email <span class="auth-required">*</span>
            </label>
            <div class="auth-input-wrapper">
              <input
                type="email"
                v-model="userRegistered.email"
                class="auth-input"
                id="email"
                placeholder="tu@email.com"
                :class="{ 'auth-input-error': $v.userRegistered.email.$error }"
              />
              <div v-if="$v.userRegistered.email.$error" class="auth-error-message">
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.userRegistered.email.required">{{
                  errorMessages.required
                }}</span>
                <span v-else-if="!$v.userRegistered.email.email">{{
                  errorMessages.email
                }}</span>
              </div>
            </div>
          </div>

          <!-- Password Field -->
          <div class="auth-form-group">
            <label for="password" class="auth-label">
              <i class="ri-lock-2-line auth-label-icon"></i>
              Contraseña <span class="auth-required">*</span>
            </label>
            <div class="auth-input-wrapper">
              <input
                v-model="userRegistered.password"
                :type="showPassword ? 'text' : 'password'"
                class="auth-input"
                id="password"
                placeholder="Mínimo 8 caracteres"
                :class="{ 'auth-input-error': $v.userRegistered.password.$error }"
              />
              <button
                type="button"
                class="auth-password-toggle"
                @click="togglePasswordVisibility"
              >
                <i
                  :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"
                ></i>
              </button>
              <div
                v-if="$v.userRegistered.password.$error"
                class="auth-error-message"
              >
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.userRegistered.password.required">{{
                  errorMessages.required
                }}</span>
                <span v-else-if="!$v.userRegistered.password.minLength">{{
                  errorMessages.minLength(
                    $v.userRegistered.password.$params.minLength.min
                  )
                }}</span>
                <span v-else-if="!$v.userRegistered.password.maxLength">{{
                  errorMessages.maxLength(
                    $v.userRegistered.password.$params.maxLength.max
                  )
                }}</span>
              </div>
            </div>
          </div>

          <!-- Confirm Password Field -->
          <div class="auth-form-group">
            <label for="repassword" class="auth-label">
              <i class="ri-lock-password-line auth-label-icon"></i>
              Confirmar Contraseña <span class="auth-required">*</span>
            </label>
            <div class="auth-input-wrapper">
              <input
                v-model="userRegistered.repassword"
                :type="showRePassword ? 'text' : 'password'"
                class="auth-input"
                id="repassword"
                placeholder="Confirma tu contraseña"
                :class="{ 'auth-input-error': $v.userRegistered.repassword.$error }"
              />
              <button
                type="button"
                class="auth-password-toggle"
                @click="toggleRePasswordVisibility"
              >
                <i
                  :class="showRePassword ? 'ri-eye-off-line' : 'ri-eye-line'"
                ></i>
              </button>
              <div
                v-if="$v.userRegistered.repassword.$error"
                class="auth-error-message"
              >
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.userRegistered.repassword.required">{{
                  errorMessages.required
                }}</span>
                <div v-else-if="!$v.userRegistered.repassword.sameAs">
                  {{ errorMessages.sameAs }}
                </div>
              </div>
            </div>
          </div>

          <!-- Autonomous Community Field -->
          <div class="auth-form-group">
            <label for="userAutonomousCommunity" class="auth-label">
              <i class="ri-map-pin-line auth-label-icon"></i>
              Comunidad Autónoma <span class="auth-required">*</span>
            </label>
            <div class="auth-select-wrapper">
              <v-select
                id="selectAutonomousCommunityOptions"
                v-model="userRegistered.autonomous_community_id"
                :options="selectAutonomousCommunityOptions"
                :reduce="(option) => option.id"
                label="name"
                placeholder="Selecciona comunidad autónoma"
                no-options-text="No se encontraron resultados"
                :class="{ 'auth-select-error': $v.userRegistered.autonomous_community_id.$error }"
              ></v-select>
            </div>
            <div
              v-if="$v.userRegistered.autonomous_community_id.$error"
              class="auth-error-message"
            >
              <i class="ri-error-warning-line"></i>
              <span v-if="!$v.userRegistered.autonomous_community_id.required">{{
                errorMessages.required
              }}</span>
            </div>
          </div>

          <!-- Center Field -->
          <div class="auth-form-group">
            <label for="userCenter" class="auth-label">
              <i class="ri-community-line auth-label-icon"></i>
              Centro <span class="auth-required">*</span>
            </label>
            <div class="auth-input-wrapper">
              <input
                type="text"
                v-model="userRegistered.center"
                class="auth-input"
                id="userCenter"
                placeholder="Introduce el nombre del centro"
                maxlength="255"
                :class="{ 'auth-input-error': $v.userRegistered.center.$error }"
              />
              <div v-if="$v.userRegistered.center.$error" class="auth-error-message">
                <i class="ri-error-warning-line"></i>
                <span v-if="!$v.userRegistered.center.required">{{
                  errorMessages.required
                }}</span>
              </div>
            </div>
          </div>

          <!-- Project Field -->
          <div class="auth-form-group">
            <label for="userProject" class="auth-label">
              <i class="ri-article-line auth-label-icon"></i>
              Convocatoria <span class="auth-required">*</span>
            </label>
            <div class="auth-select-wrapper">
              <v-select
                id="selectProjectOptions"
                v-model="userRegistered.project_id"
                :options="selectProjectOptions"
                :reduce="(option) => option.id"
                label="name"
                placeholder="Selecciona convocatoria"
                no-options-text="No se encontraron resultados"
                :class="{ 'auth-select-error': $v.userRegistered.project_id.$error }"
              ></v-select>
            </div>
            <div
              v-if="$v.userRegistered.project_id.$error"
              class="auth-error-message"
            >
              <i class="ri-error-warning-line"></i>
              <span v-if="!$v.userRegistered.project_id.required">{{
                errorMessages.required
              }}</span>
            </div>
          </div>

          <!-- Use Field -->
          <div class="auth-form-group">
            <label for="userUse" class="auth-label">
              <i class="ri-align-justify auth-label-icon"></i>
              Uso <span class="auth-required">*</span>
            </label>
            <div class="auth-textarea-wrapper">
              <textarea
                v-model="userRegistered.use"
                class="auth-textarea"
                :maxlength="500"
                id="userUse"
                rows="4"
                placeholder="Introduce uso al que vas a destinar la plataforma"
                :class="{ 'auth-input-error': $v.userRegistered.use.$error }"
              ></textarea>
              <small class="auth-char-count"
                >{{ userRegistered.use.length }}/500</small
              >
            </div>
            <div v-if="$v.userRegistered.use.$error" class="auth-error-message">
              <i class="ri-error-warning-line"></i>
              <span v-if="!$v.userRegistered.use.required">{{
                errorMessages.required
              }}</span>
            </div>
          </div>

          <!-- Submit Button -->
          <button
            class="auth-submit-btn"
            type="submit"
            :disabled="loading"
          >
            <span v-if="loading" class="auth-spinner"></span>
            <span v-else>
              <i class="ri-user-add-line"></i>
              Enviar solicitud
            </span>
          </button>
        </form>

        <!-- Login Link -->
        <div class="auth-footer">
          <p class="auth-footer-text">
            ¿Ya tienes una cuenta?
            <router-link
              tag="a"
              to="/login"
              class="auth-link auth-link-register"
            >
              Inicia sesión aquí
            </router-link>
          </p>
          <p class="auth-footer-copyright">
            © 2026 CEX. Crafted with <i class="ri-heart-fill" style="color: #e74c3c;"></i> by Quelinka
          </p>
        </div>
      </div>

      <!-- Decorative side panel -->
      <div class="auth-side-panel">
        <div class="auth-side-content">
          <h2 class="auth-side-title">Únete a nosotros</h2>
          <p class="auth-side-description">
            Solicita acceso a la plataforma y comienza a gestionar tus proyectos de forma eficiente
          </p>
          <div class="auth-side-features">
            <div class="auth-feature-item">
              <i class="ri-shield-star-line"></i>
              <span>Proceso seguro y verificado</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-mail-check-line"></i>
              <span>Recibirás confirmación por email</span>
            </div>
            <div class="auth-feature-item">
              <i class="ri-time-line"></i>
              <span>Respuesta rápida a tu solicitud</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Institutional Logos Banner -->
    <div class="auth-logos-banner-wrapper">
      <InstitutionalLogosBanner variant="default" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.auth-card-register {
  max-height: 90vh;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(110, 166, 55, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #6ea637;
    border-radius: 4px;
    
    &:hover {
      background: #558b2f;
    }
  }
}

.auth-required {
  color: #e74c3c;
  font-weight: 600;
}

.auth-select-wrapper {
  position: relative;
  
  :deep(.vs__dropdown-toggle) {
    padding: 1rem 1.25rem;
    border: 2px solid rgba(110, 166, 55, 0.2);
    border-radius: 12px;
    min-height: 50px;
    transition: all 0.3s ease;
    
    &:focus-within {
      border-color: #6ea637;
      box-shadow: 0 0 0 4px rgba(110, 166, 55, 0.1);
    }
  }
  
  :deep(.vs__search) {
    margin: 0;
    padding: 0;
    font-size: 1rem;
    color: #2c3e50;
  }
  
  :deep(.vs__selected-options) {
    padding: 0;
  }
  
  :deep(.vs__selected) {
    margin: 0;
    padding: 0;
    color: #2c3e50;
  }
  
  :deep(.vs__actions) {
    padding: 0 0.5rem;
  }
  
  :deep(.vs__clear) {
    fill: #6ea637;
  }
  
  :deep(.vs__open-indicator) {
    fill: #6ea637;
  }
  
  &.auth-select-error :deep(.vs__dropdown-toggle) {
    border-color: #e74c3c;
    box-shadow: 0 0 0 4px rgba(231, 76, 60, 0.1);
  }
}

.auth-textarea-wrapper {
  position: relative;
}

.auth-textarea {
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid rgba(110, 166, 55, 0.2);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #ffffff;
  color: #2c3e50;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #6ea637;
    box-shadow: 0 0 0 4px rgba(110, 166, 55, 0.1);
  }

  &::placeholder {
    color: #95a5a6;
  }

  &.auth-input-error {
    border-color: #e74c3c;
    box-shadow: 0 0 0 4px rgba(231, 76, 60, 0.1);
  }
}

.auth-char-count {
  display: block;
  text-align: right;
  margin-top: 0.5rem;
  color: #95a5a6;
  font-size: 0.875rem;
}
</style>
